import React, { useState } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { useRouteMatch } from 'react-router';
import {
  Table,
  Space,
  Button,
  Modal,
  Form,
  FormInstance,
  Divider,
  Row,
  Col,
  Typography,
} from 'antd';

import CreateRateModal from './CreateRateModal';
import UpdateRateModal from './UpdateRateModal';
import { quantityFormatter } from '@/utils/number';
import { COLUMNS_WIDTH } from '@/utils/table';
import { getTableScrollX } from '@/utils/table';
import {
  useDeleteDocumentTypeRate,
  useDocumentTypeRates,
} from '@/core/domain/enterprise/DocumentTypeRates/DocumentTypeRates.hooks';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { ColumnsType } from 'antd/lib/table';
import { DocumentTypeRate } from '@/core/domain/enterprise/DocumentTypeRates/DocumentTypeRates.types';
import {
  CreateDocumentTypeRequest,
  UpdateDocumentTypeRequest,
} from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import { DeleteIcon, EditIcon } from '@/components/_common';

interface Props {
  form: FormInstance<CreateDocumentTypeRequest | UpdateDocumentTypeRequest>;
}

function AccountsDocumentTypesRates({ form }: Props) {
  const intl = useIntl();
  const { documentTypeId } = useRouteMatch<{ documentTypeId: string }>().params;

  const [modalCreateRateVisible, setModalCreateRateVisible] = useState(false);
  const [modalUpdateRateVisible, setModalUpdateRateVisible] = useState(false);

  const [selectedRate, setSelectedRate] = useState<DocumentTypeRate>();

  const { data, isFetching } = useDocumentTypeRates(Number(documentTypeId));

  const rates = flatInfiniteData(data);

  const removeMutation = useDeleteDocumentTypeRate(Number(documentTypeId));

  const notGenerateFinancial = Form.useWatch('notGenerateFinancial', form);

  function toggleModalCreateRateVisible() {
    setModalCreateRateVisible(!modalCreateRateVisible);
  }

  function toggleModalUpdateRateVisible(rate: DocumentTypeRate) {
    if (selectedRate) {
      setSelectedRate(undefined);
    } else {
      setSelectedRate(rate);
    }

    setModalUpdateRateVisible(!modalUpdateRateVisible);
  }

  function handleRemoveRate(rate: DocumentTypeRate) {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'documentTypes.rate.remove.modal.title',
      }),
      content: `${intl.formatMessage({
        id: 'documentTypes.rate.remove.modal.content',
      })}`,
      onOk: async () => {
        try {
          await removeMutation.mutateAsync({ ...rate });
        } catch {}
      },
      okText: intl.formatMessage({
        id: 'documentType.rate.remove.modal.button.ok',
      }),
      okType: 'danger',
    });
  }

  function handleSortNumberInstallments(
    a: DocumentTypeRate,
    b: DocumentTypeRate,
  ) {
    if (a.numberInstallments < b.numberInstallments) {
      return -1;
    }
    if (a.numberInstallments > b.numberInstallments) {
      return 1;
    }
    return 0;
  }

  const columns: ColumnsType<DocumentTypeRate> = [
    {
      dataIndex: 'numberInstallments',
      title: intl.formatMessage({
        id: 'documentTypes.rate.list.numberInstallments',
      }),
      width: COLUMNS_WIDTH.default,
    },
    {
      dataIndex: 'percentRate',
      title: intl.formatMessage({
        id: 'documentTypes.rate.list.percentRate',
      }),
      width: COLUMNS_WIDTH.default,
      render: (value) => quantityFormatter(value),
    },
    {
      title: intl.formatMessage({
        id: 'documentTypes.rate.list.header.actions',
      }),
      width: 100,
      fixed: 'right',
      align: 'right',
      render: (_, record) => (
        <Space>
          <Space>
            <EditIcon
              iconProps={{
                onClick: () => toggleModalUpdateRateVisible(record),
              }}
            />

            <Divider type="vertical" />

            <DeleteIcon onClick={() => handleRemoveRate(record)} />
          </Space>
        </Space>
      ),
    },
  ];

  return (
    <>
      <Row gutter={[8, 8]}>
        <Col span={24} md={18} lg={20} xxl={21}>
          <Typography.Title level={4}>
            <FormattedMessage id="documentTypes.rate.list.title" />
          </Typography.Title>
        </Col>

        <Col span={24} md={6} lg={4} xxl={3}>
          <Button
            block
            type="primary"
            hidden={notGenerateFinancial}
            onClick={toggleModalCreateRateVisible}
          >
            <FormattedMessage id="documentTypes.rate.list.create" />
          </Button>
        </Col>

        {notGenerateFinancial ? null : (
          <Col span={24}>
            <Table
              className="table-small"
              columns={columns}
              dataSource={rates.sort(handleSortNumberInstallments)}
              loading={isFetching}
              rowKey={(row) => row.id}
              pagination={false}
              scroll={{ x: getTableScrollX(columns) }}
            />
          </Col>
        )}
      </Row>

      <CreateRateModal
        visible={modalCreateRateVisible}
        onCancel={toggleModalCreateRateVisible}
        ratesLength={rates.length}
      />

      <UpdateRateModal
        visible={modalUpdateRateVisible}
        onCancel={toggleModalUpdateRateVisible}
        rate={selectedRate}
      />
    </>
  );
}

export default AccountsDocumentTypesRates;
